'use client';

import React, { type ReactElement, type ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { type SearchBasicResult } from '@/common/types/searchResult.type';
import classNames from 'classnames';
import { type SearchResultPayloadType } from '@/common/apis/home/searchProducts';
import Button from '@/common/components/button';
import Hamburger from '@/common/components/hamburger';
import StoreWidget from '@/common/components/store-widget';
import Wrapper from '@/common/components/wrapper/wrapper.component';
import { useBackNavigation } from '@/common/hooks/use-back-navigation.hook';
import ArrowRightIcon from '~/public/assets/images/svg/arrow.svg';

const CartWidget = dynamic(
  async () => await import('@/common/components/cart-widget'),
  {
    ssr: false,
  }
);

interface LayoutChildrenClassNames {
  storeWidgetTitleClassName?: string;
  storeWidgetBtnLinkClassName?: string;
  arrowClassName?: string;
  hamburgerClassName?: string;
  cartClassName?: string;
}

export interface AppbarProps {
  from?: string;
  onBack?: () => void;
  storeWidget?: boolean;
  hamburger?: boolean;
  cartWidget?: boolean;
  title?: ReactNode;
  titlePosition?: 'center' | 'right';
  className?: string;
  wrapperClassName?: string;
  layoutChildrenClassNames?: LayoutChildrenClassNames;
  showSearch?: boolean;
  fixedHeader?: boolean;
  onSearch?: (
    params: SearchResultPayloadType,
    suggestions?: SearchBasicResult | undefined
  ) => void;
  logo?: boolean;
  searchValue?: string;
}

export default function Appbar({
  from,
  onBack,
  storeWidget,
  cartWidget = true,
  hamburger = true,
  title,
  titlePosition,
  className,
  layoutChildrenClassNames,
}: AppbarProps): ReactElement {
  const { handleBack } = useBackNavigation();

  const onBackHandler = () => {
    if (onBack) {
      onBack();
    }
    handleBack();
  };

  return (
    <div className={classNames('mt-2 h-[52px] w-full', className)}>
      <Wrapper className="px-3">
        <div
          className={classNames('grid h-[52px] grid-cols-3 items-center', {
            '!grid-cols-2': storeWidget || !title,
            '!grid grid-cols-[40px_2fr_1fr]': titlePosition === 'right',
          })}
        >
          <div className="flex items-center justify-start gap-x-2">
            <Button
              className="flex !h-10 w-10 items-center justify-center"
              type="link"
              onClick={onBackHandler}
            >
              <ArrowRightIcon
                className={classNames(
                  'h-3 w-[8px] text-black-default',
                  layoutChildrenClassNames?.arrowClassName
                )}
              />
            </Button>
            {storeWidget && (
              <StoreWidget
                titleClassName={
                  layoutChildrenClassNames?.storeWidgetTitleClassName
                }
                btnLinkClassName={
                  layoutChildrenClassNames?.storeWidgetBtnLinkClassName
                }
              />
            )}
          </div>
          {title && (
            <h3
              className={classNames(
                'text-nowrap text-center text-[14px] font-medium text-black-default'
              )}
            >
              {title}
            </h3>
          )}
          <div className="flex items-center justify-end gap-x-2">
            {cartWidget && (
              <CartWidget
                id="store-click-cart-v1"
                iconClassName={layoutChildrenClassNames?.cartClassName}
              />
            )}
            {hamburger && (
              <span className="hidden lg:inline-flex">
                <Hamburger
                  iconClassName={layoutChildrenClassNames?.hamburgerClassName}
                />
              </span>
            )}
          </div>
        </div>
      </Wrapper>
    </div>
  );
}
